import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle2, AlertTriangle, Zap, Sparkles, Scissors, FileText, Wrench } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICON = { remove: Scissors, clarify: FileText, other: Wrench };
const ACTION_LABEL = { remove: 'Remove from scope', clarify: 'Clarification', other: 'Adjustment' };

function Column({ title, subtitle, icon: Icon, rows, renderRow, emptyNote, testid }) {
  return (
    <div className="flex-1 min-w-[280px]" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040] mb-1">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="text-[10px] text-slate-400 mb-2">{subtitle}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic">{emptyNote}</div>
      ) : (
        <div className="space-y-1.5">{rows.map(renderRow)}</div>
      )}
    </div>
  );
}

export function ChangePatternsInsightsCard({ days = 365, minVotes = 2 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/scope-pack/change-patterns`, {
          params: { days, min_votes: minVotes }, withCredentials: true,
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, minVotes]);

  if (loading) return null;
  if (!data) return null;
  const anyContent =
    (data.universally_loved?.length || 0) +
    (data.most_contested?.length || 0) +
    (data.most_accepted_changes?.length || 0);
  if (!anyContent) return null;

  return (
    <Card data-testid="change-patterns-insights-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Scope Pack Patterns
          <Badge variant="secondary" className="ml-1 text-[10px] bg-slate-100 text-slate-600 font-normal">
            last {data.window_days}d &middot; min {data.min_votes_threshold} votes
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-6">
        <Column
          title="Universally loved"
          subtitle="Lead proposals with these"
          icon={CheckCircle2}
          rows={data.universally_loved || []}
          emptyNote="Not enough votes yet."
          testid="col-loved"
          renderRow={(r) => (
            <div key={r.component_name}
              className="flex items-center gap-2 px-2 py-1.5 rounded border border-emerald-100 bg-emerald-50/40">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#0F2040] font-medium truncate">{r.component_name}</div>
                <div className="text-[10px] text-slate-500">{r.total_votes} votes</div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 px-1.5 py-0 text-[10px]">
                {r.good_rate}% good
              </Badge>
            </div>
          )}
        />
        <Column
          title="Most contested"
          subtitle="Pre-empt objections in the pitch"
          icon={AlertTriangle}
          rows={data.most_contested || []}
          emptyNote="No contested components."
          testid="col-contested"
          renderRow={(r) => (
            <div key={r.component_name}
              className="flex items-center gap-2 px-2 py-1.5 rounded border border-amber-100 bg-amber-50/40">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#0F2040] font-medium truncate">{r.component_name}</div>
                <div className="text-[10px] text-slate-500">
                  {r.drop} drops &middot; {r.question} questions &middot; {r.total_votes} votes
                </div>
              </div>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 px-1.5 py-0 text-[10px]">
                {r.contested_rate}% contested
              </Badge>
            </div>
          )}
        />
        <Column
          title="Most accepted changes"
          subtitle="Stakeholders tend to say yes to these"
          icon={Zap}
          rows={data.most_accepted_changes || []}
          emptyNote="No change-request history yet."
          testid="col-changes"
          renderRow={(r) => {
            const Icon = ACTION_ICON[r.proposed_action] || Wrench;
            return (
              <div key={`${r.component_name}-${r.proposed_action}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-sky-100 bg-sky-50/40">
                <Icon className="w-3 h-3 text-sky-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#0F2040] font-medium truncate">{r.component_name}</div>
                  <div className="text-[10px] text-slate-500">
                    {ACTION_LABEL[r.proposed_action] || r.proposed_action} &middot;
                    {' '}{r.accepted}/{r.proposed} accepted
                  </div>
                </div>
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 px-1.5 py-0 text-[10px]">
                  {r.accept_rate}% accept
                </Badge>
              </div>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}
